/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unstable-nested-components */
import React, { useMemo } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { ChartBarIcon } from "@heroicons/react/24/outline";

export const COLORS = [
  "#7C3AED", "#F59E0B", "#EC4899", "#06B6D4", "#10B981", "#F97316", "#EF4444", "#6366F1",
];

type Props = {
  data: any;
  bucket?: string;
};

const TrendsChart = ({ data, bucket }: Props) => {
  const metricKeys = useMemo(() => {
    if (!data || data.length === 0) return [];
    const keys = new Set<string>();
    data.forEach((d: any) => {
      Object.keys(d).forEach((k) => { if (k !== "date") keys.add(k); });
    });
    return Array.from(keys);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="h-full bg-white w-full rounded-lg flex flex-col py-20 items-center justify-center p-8 border border-gray-100 shadow-sm">
        <div className="bg-gray-50 p-4 rounded-full mb-4">
          <ChartBarIcon className="w-10 h-10 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-1">No chart data available</h3>
        <p className="text-sm text-gray-500 text-center max-w-xs">
          There&apos;s no data available for the selected events in this time period. Try selecting different events or adjusting the date range.
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400} className="bg-white rounded-lg">
      <ComposedChart
        data={data}
        margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
      >
        <defs>
          {metricKeys.map((key, idx) => {
            const color = idx < COLORS.length ? COLORS[idx] : "#6B7280";
            return (
              <linearGradient key={`gradient-${key}`} id={`area-${idx}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.15} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            );
          })}
        </defs>

        <CartesianGrid stroke="#F3F4F6" vertical={false} />

        <XAxis
          dataKey="date"
          padding={{ left: 10, right: 10 }}
          type="category"
          tickLine={false}
          axisLine={{ stroke: "#E5E7EB" }}
          allowDuplicatedCategory={false}
          tick={{ fontFamily: "Nunito", fontSize: 12, fill: "#9CA3AF" }}
          interval="preserveStartEnd"
          minTickGap={80}
        />

        <YAxis
          type="number"
          domain={[0, "auto"]}
          tickMargin={8}
          tick={{ fontFamily: "Nunito", fontSize: 12, fill: "#9CA3AF" }}
          tickCount={6}
          axisLine={false}
          tickLine={false}
          width={45}
        />

        <Tooltip
          cursor={{ stroke: "#D1D5DB", strokeWidth: 1 }}
          content={<CustomTooltip bucket={bucket} />}
        />

        {metricKeys.map((key, idx) => {
          const color = idx < COLORS.length ? COLORS[idx] : "#6B7280";
          return (
            <Area
              key={`area-${key}`}
              dataKey={key}
              type="monotone"
              fill={`url(#area-${idx})`}
              stroke="none"
              isAnimationActive={false}
            />
          );
        })}

        {metricKeys.map((key, idx) => {
          const color = idx < COLORS.length ? COLORS[idx] : "#6B7280";
          return (
            <Line
              key={`line-${key}`}
              dataKey={key}
              type="monotone"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "#fff", fill: color }}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const bucketLabel: Record<string, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
};

export const CustomTooltip = ({
  label,
  payload,
  active,
  bucket,
}: any) => {
  if (!active || !payload || payload.length === 0) return null;

  // Deduplicate — ComposedChart sends both Area + Line entries for each metric
  const seen = new Set<string>();
  const unique = payload.filter((p: any) => {
    if (seen.has(p.dataKey)) return false;
    seen.add(p.dataKey);
    return true;
  });

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 px-4 py-3 min-w-[180px]">
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
        {label}
      </div>
      <div className="space-y-2">
        {unique.map((p: any, idx: number) => (
          <div key={idx}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span
                className="h-2 w-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: p.color || p.stroke }}
              />
              <span className="text-xs font-medium text-gray-600">{p.name}</span>
            </div>
            <div className="pl-3.5 text-sm font-bold text-gray-900 tabular-nums">
              {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrendsChart;
